/**
 * Department dropdown backed by {@link useDepartments}.
 */

import * as React from 'react';
import { Select } from '@/components/forms/Select';
import { useDepartments } from '@/hooks/useDepartments';
import { getErrorMessage } from '@/utils/errors-utils';

/** Props for {@link DepartmentSelect}. */
interface DepartmentSelectProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  placeholder?: string;
  country?: string;
  size?: 'sm' | 'md' | 'lg';
  inputSize?: 'sm' | 'md' | 'lg';
  className?: string;
  /** When set, only department codes in this list are shown. */
  allowedCodes?: ReadonlyArray<string>;
}

/** Select for French departments, with optional code-whitelist filtering. */
export const DepartmentSelect: React.FC<DepartmentSelectProps> = ({
  label = 'Département',
  value,
  onChange,
  required = false,
  placeholder = 'Sélectionner un département',
  country = 'FR',
  size,
  inputSize,
  className,
  allowedCodes,
}) => {
  const { data: departments = [], isLoading, error } = useDepartments(country);

  const options = React.useMemo(() => {
    const filtered = allowedCodes
      ? departments.filter((dept) => allowedCodes.includes(dept.code))
      : departments;
    return filtered.map((dept) => ({
      value: dept.code,
      label: dept.name,
    }));
  }, [departments, allowedCodes]);

  return (
    <Select
      label={label}
      value={value}
      onChange={onChange}
      options={options}
      required={required}
      placeholder={placeholder}
      disabled={isLoading}
      error={error ? getErrorMessage(error) : undefined}
      size={size ?? inputSize}
      className={className}
    />
  );
};
